import { useClientPortalAPI } from "@certn/api";
import { Text, Stack, Link, BasicButton } from "@certn/cascadia";
import {
  Container,
  Layout,
  MediaQuery,
  Tabs,
  Tab,
  TabList,
  TabPanel,
  NewBadge,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useResolvedPath,
  useSearchParams,
} from "react-router-dom";
import { createIncludesTabIdMapper } from "@certn/utils";

import { ActiveCasesTab } from "./tabs/ActiveCasesTab";
import { DoneCasesTab } from "./tabs/DoneCasesTab";
import { ReviewCasesTab } from "./tabs/ReviewCasesTab";
import { LegacyCasesHRTab } from "./tabs/LegacyCasesHRTab";
import { LegacyCasesPMTab } from "./tabs/LegacyCasesPMTab";
import { AppFooterLinks } from "../../components/app-footer/AppFooterLinks";
import { AppHeader } from "../../components/app-header/AppHeader";
import { AppNavigation } from "../../components/app-navigation/AppNavigation";
import { PageTitle } from "../../components/page-title/PageTitle";
import { GroupOverviewDropdownMenu } from "../groups/components/GroupOverviewDropdownMenu";
import { DownloadCSVModal } from "./components/DownloadCSVModal";
import { getStatusFromPath, buildCaseTabHref } from "./utils";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useClientAccount } from "../../hooks/useClientAccount";

export function GroupOverviewPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { clientPortalLegacyCases } = useFlags();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const customerGroupId = searchParams.get("customer_group_id");
  const url = useResolvedPath("").pathname;
  const isGroupOverview = pathname.includes("/group-overview");
  const { client, customerGroups } = useClientAccount();

  // Check for legacy_id and permissible purposes to conditionally render Historic tabs
  const hasLegacyId = client?.legacy_id && client.legacy_id.trim() !== "";
  const permissiblePurposes =
    customerGroups?.map((group) => group.permissible_purpose) || [];
  const hasEmploymentPurpose = permissiblePurposes.includes("EMPLOYMENT");
  const hasTenancyPurpose = permissiblePurposes.includes("TENANCY");

  // Only show Historic tabs if legacy_id exists and has the appropriate permissible purpose
  const showHistoricHR = hasLegacyId && hasEmploymentPurpose;
  const showHistoricPM = hasLegacyId && hasTenancyPurpose;

  const mapSelectedTabId = createIncludesTabIdMapper(
    [
      { substring: "/review", id: "review-tab" },
      { substring: "/done", id: "done-tab" },
      { substring: "/legacy-hr", id: "legacy-hr-tab" },
      { substring: "/legacy-pm", id: "legacy-pm-tab" },
    ],
    "active-tab"
  );
  const { data: caseCounts } = useClientPortalAPI(
    isGroupOverview && customerGroupId
      ? `/api/internal/client-portal/case/counts/filtered/${customerGroupId}/`
      : "/api/internal/client-portal/case/counts/"
  );
  const { data: groupDetails, mutate } = useClientPortalAPI(
    isGroupOverview && customerGroupId
      ? `/api/internal/client-portal/customer-groups/${customerGroupId}/`
      : null
  );

  const groupName = isGroupOverview ? groupDetails?.name : t("Cases");
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);

  const currentStatus = getStatusFromPath(pathname, url);
  const canDownloadCSV =
    (currentStatus === "active" ||
      currentStatus === "review" ||
      currentStatus === "done") &&
    !isGroupOverview;

  const buildTabHref = (path: string) =>
    buildCaseTabHref(url, path, searchParams);

  useEffect(() => {
    if (isGroupOverview && !customerGroupId) {
      navigate("/cases");
    }
  }, [isGroupOverview, customerGroupId, navigate]);

  if (isGroupOverview && !customerGroupId) {
    return null;
  }

  return (
    <Layout
      layout="narrow"
      header={<AppHeader />}
      navigation={<AppNavigation />}
      footer={<AppFooterLinks />}
    >
      <Container layout="full-width">
        <div className={styles.title}>
          <PageTitle>{groupName}</PageTitle>
          {customerGroupId && groupDetails && (
            <GroupOverviewDropdownMenu
              groupId={groupDetails.id}
              isFavourite={!!groupDetails.is_favourite}
              mutate={() => mutate()}
            />
          )}

          {canDownloadCSV && (
            <div className={styles.downloadButton}>
              <BasicButton
                size="small"
                testID="download-csv-button"
                onPress={() => setIsDownloadModalOpen(true)}
                leftIcon="download"
              >
                {t("Download CSV")}
              </BasicButton>
            </div>
          )}
        </div>
        <MediaQuery sm type="hide">
          <Stack orientation="horizontal" gap="space-2">
            <Text color="text-weak">
              <Trans>
                A convenient list of all cases across the groups you have access
                to.
              </Trans>
            </Text>
            <Link
              target="_blank"
              href="https://portal.certn.co/hc/en-us/articles/27892000211475-Manage-your-cases"
              textStyle="global/body-secondary"
              linkStyle="monochrome"
              testID="group-overview-learn-more-link"
            >
              <Trans>Learn more</Trans>
            </Link>
          </Stack>
        </MediaQuery>
        <Tabs
          key={pathname}
          selectedKey={mapSelectedTabId(pathname)}
          testID="case-list-tabs"
        >
          <TabList aria-label={t("Case statuses")}>
            <Tab
              id="active-tab"
              href={buildTabHref("")}
              testID="case-list-active-tab"
            >
              {t("Active")}
            </Tab>
            <Tab
              id="review-tab"
              href={buildTabHref("/review")}
              testID="case-list-review-tab"
            >
              {t("Review")}
              {caseCounts && caseCounts.review > 0 && (
                <NewBadge testID="case-list-review-tab-badge">
                  {caseCounts.review}
                </NewBadge>
              )}
            </Tab>
            <Tab
              id="done-tab"
              href={buildTabHref("/done")}
              testID="case-list-done-tab"
            >
              {t("Done")}
            </Tab>
            {clientPortalLegacyCases && (
              <>
                {showHistoricHR && (
                  <Tab
                    id="legacy-hr-tab"
                    href={buildTabHref("/legacy-hr")}
                    testID="case-list-legacy-hr-tab"
                  >
                    {t("Historical HR")}
                  </Tab>
                )}
                {showHistoricPM && (
                  <Tab
                    id="legacy-pm-tab"
                    href={buildTabHref("/legacy-pm")}
                    testID="case-list-legacy-pm-tab"
                  >
                    {t("Historical PM")}
                  </Tab>
                )}
              </>
            )}
          </TabList>
          <TabPanel id={mapSelectedTabId(pathname)}>
            <Routes>
              <Route path="" element={<ActiveCasesTab />} />
              <Route path="/review" element={<ReviewCasesTab />} />
              <Route path="/done" element={<DoneCasesTab />} />
              <Route path="/legacy-hr" element={<LegacyCasesHRTab />} />
              <Route path="/legacy-pm" element={<LegacyCasesPMTab />} />
            </Routes>
          </TabPanel>
        </Tabs>
        {canDownloadCSV && (
          <DownloadCSVModal
            isOpen={isDownloadModalOpen}
            onClose={() => setIsDownloadModalOpen(false)}
          />
        )}
      </Container>
    </Layout>
  );
}

const styles = {
  title: css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "var(--space-2)",
  }),
  downloadButton: css({
    display: "flex",
    marginLeft: "auto",
  }),
};
